import { useState, useEffect, useCallback } from 'react';
import restaurantAPI from '../api/restaurantAPI';
import { getUserIdFromToken } from '../api/authAPI';
import { useAuth } from '../context/AuthContext';

const ACTIVE_SHIFT_CASHIER_ID_KEY = 'activeShiftCashierId';

/**
 * Custom hook for managing restaurant cashier shifts
 * Handles: start shift, end shift, get active shift status
 * Uses new /api/restaurant/shift/* endpoints
 */
export const useShift = () => {
  const { getAccessToken } = useAuth();
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const extractPayload = useCallback((response) => {
    if (!response) return {};
    if (response.data && typeof response.data === 'object') return response.data;
    if (typeof response === 'object') return response;
    return {};
  }, []);

  // Get current user from localStorage
  const getCurrentUser = useCallback(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return null;
    try {
      return JSON.parse(storedUser);
    } catch {
      return null;
    }
  }, []);

  // Get token from AuthContext
  const getToken = useCallback(() => {
    return getAccessToken();
  }, [getAccessToken]);

  const getCachedCashierId = useCallback(() => {
    return localStorage.getItem(ACTIVE_SHIFT_CASHIER_ID_KEY) || '';
  }, []);

  const setCachedCashierId = useCallback((cashierId) => {
    if (!cashierId) return;
    localStorage.setItem(ACTIVE_SHIFT_CASHIER_ID_KEY, String(cashierId));
  }, []);

  const clearCachedCashierId = useCallback(() => {
    localStorage.removeItem(ACTIVE_SHIFT_CASHIER_ID_KEY);
  }, []);

  // Get active shift for current user, then fallback to cached/known cashier employeeId.
  const checkShiftStatus = useCallback(async () => {
    try {
      setLoading(true);
      const user = getCurrentUser();
      const token = getToken();

      if (!token) {
        setError('User not authenticated');
        return null;
      }

      let userId = user?.id || user?._id;
      
      // Fallback: try to get user ID from JWT token
      if (!userId) {
        userId = getUserIdFromToken();
      }

      if (!userId) {
        setError('User ID not found - please log in again');
        return null;
      }

      // Call new restaurant endpoint to get active shift
      const response = await restaurantAPI.shifts.getActive();
      const payload = extractPayload(response);

      if (payload.shift) {
        setShift(payload.shift);
        const shiftCashierEmployeeId = payload.shift?.cashier?.employeeId;
        if (shiftCashierEmployeeId) {
          setCachedCashierId(shiftCashierEmployeeId);
        }
        setError(null);
        return payload.shift;
      } else {
        const fallbackCashierId =
          getCachedCashierId() ||
          user?.employeeId ||
          user?.userData?.employeeId ||
          '';

        if (fallbackCashierId) {
          try {
            const byCashierResponse = await restaurantAPI.shifts.getActiveByCashier(fallbackCashierId);
            const byCashierPayload = extractPayload(byCashierResponse);
            if (byCashierPayload.shift) {
              setShift(byCashierPayload.shift);
              setCachedCashierId(fallbackCashierId);
              setError(null);
              return byCashierPayload.shift;
            }
          } catch {
            // Ignore fallback failures and continue as no-active-shift.
          }
        }

        setShift(null);
        return null;
      }
    } catch (err) {
      // No active shift is not an error, just return null
      setShift(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [getCurrentUser, getToken, getCachedCashierId, setCachedCashierId, extractPayload]);

  // Start a new shift with denomination breakdown
  const startShift = useCallback(async (cashierId, pin, startingDenominations = {}) => {
    try {
      setLoading(true);
      setError(null);

      const token = getToken();

      if (!token) {
        setError('User not authenticated');
        return null;
      }

      if (!cashierId || !pin) {
        setError('Cashier ID and PIN are required');
        return null;
      }

      const trimmedPin = String(pin).trim();
      console.log('trimmedPin', trimmedPin);
      if (!trimmedPin || trimmedPin.length < 4) {
        throw new Error('PIN must be exactly 4 digits');
      }

      // Call new restaurant endpoint
      const response = await restaurantAPI.shifts.open(
        cashierId,
        pin,
        startingDenominations
      );

      const payload = extractPayload(response);
      setShift(payload.shift || null);
      setCachedCashierId(cashierId);
      setError(null);
      return payload;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to start shift';
      setError(errorMessage);
      console.error('Error starting shift:', err);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getToken, extractPayload, setCachedCashierId]);

  // End the current shift with denomination breakdown
  const endShift = useCallback(async (cashierPin, closingDenominations = {}, varianceNote = '') => {
    try {
      setLoading(true);
      setError(null);

      const token = getToken();

      if (!token) {
        setError('User not authenticated');
        return null;
      }

      if (!shift) {
        setError('No active shift found');
        return null;
      }

      if (!cashierPin || cashierPin.length < 4) {
        setError('Valid PIN is required');
        return null;
      }

      // Call new restaurant endpoint
      const response = await restaurantAPI.shifts.close(
        shift._id,
        cashierPin,
        closingDenominations,
        varianceNote
      );

      const payload = extractPayload(response);

      setShift(null);
      clearCachedCashierId();
      setError(null);
      return payload;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to end shift';
      setError(errorMessage);
      console.error('Error ending shift:', err);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [shift, getToken, extractPayload, clearCachedCashierId]);

  // Initialize: check shift status when component mounts
  useEffect(() => {
    const user = getCurrentUser();
    const token = getToken();
    
    // Check for active shift if user is authenticated
    if (user && token) {
      checkShiftStatus();
    }
  }, [getCurrentUser, getToken, checkShiftStatus]);

  return {
    shift,
    loading,
    error,
    isShiftActive: !!shift,
    startShift,
    endShift,
    checkShiftStatus,
    setShiftData: (nextShift) => setShift(nextShift || null),
    clearShift: () => setShift(null),
    clearError: () => setError(null)
  };
};

export default useShift;
